/**
 * PartsGrossBreakdown Component
 *
 * Two pie charts for parts department analysis:
 * 1. Gross by Pay Type (6 categories)
 * 2. Controllable Expenses as % of total gross (Tools, Advertising, Policy, Warranty Rejection, Postage)
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { CogIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface PartsGrossByPayType {
  customerParts: number;
  warrantyParts: number;
  internalParts: number;
  counterRetail: number;
  counterWholesale: number;
  otherParts: number;
  inventoryAdjustment: number;
}

export interface PartsSalesByPayType {
  customerParts: number;
  warrantyParts: number;
  internalParts: number;
  counterRetail: number;
  counterWholesale: number;
  otherParts: number;
}

export interface PartsControllableExpense {
  accountCode: string;
  description: string;
  amount: number;
}

interface PartsGrossBreakdownProps {
  partsGrossByPayType: PartsGrossByPayType | null;
  partsSalesByPayType?: PartsSalesByPayType | null;
  partsControllableExpenses?: PartsControllableExpense[] | null;
  totalPartsGross: number;
  year: number;
  month: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  lastRefreshed?: string;
}

const PAY_TYPE_CONFIG = [
  { key: 'customerParts' as const, label: 'Customer Parts', color: '#3b82f6' },
  { key: 'warrantyParts' as const, label: 'Warranty Parts', color: '#f59e0b' },
  { key: 'internalParts' as const, label: 'Internal Parts', color: '#10b981' },
  { key: 'counterRetail' as const, label: 'Counter Retail', color: '#8b5cf6' },
  { key: 'counterWholesale' as const, label: 'Counter Wholesale', color: '#06b6d4' },
  { key: 'otherParts' as const, label: 'Other / Inv. Adj.', color: '#9ca3af' },
];

// --- Expense Chart Config ---
const EXPENSE_COLORS: Record<string, string> = {
  '061F': '#ef4444',   // red-500 - Tools & Supplies
  '062F': '#f97316',   // orange-500 - Advertising
  '067F': '#8b5cf6',   // violet-500 - Policy Expense
  '069F': '#ec4899',   // pink-500 - Warranty Rejection
  '075F': '#14b8a6',   // teal-500 - Postage & Freight
};
const REMAINING_GROSS_COLOR = '#e5e7eb'; // gray-200

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyShort(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

const PayTypeTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1">{data.label}</p>
        <p className="text-sm text-gray-600">
          Gross:{' '}
          <span className="font-medium text-blue-600">
            {formatCurrency(data.rawValue)}
          </span>
        </p>
        {data.salesValue > 0 && (
          <p className="text-sm text-gray-600">
            Sales:{' '}
            <span className="font-medium text-gray-800">
              {formatCurrency(data.salesValue)}
            </span>
          </p>
        )}
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        {data.gpPercent !== null && (
          <p className="text-sm text-gray-600">
            GP%:{' '}
            <span className={`font-medium ${data.gpPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {data.gpPercent.toFixed(1)}%
            </span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

const PayTypeLegend = (props: any) => {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-col justify-center gap-2 pl-2">
      {payload.map((entry: any, index: number) => {
        const data = entry.payload;
        return (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <div className="text-sm">
              <span className="text-gray-700">{data.label}</span>
              {data.gpPercent !== null && (
                <span className={`ml-1 font-semibold ${data.gpPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {data.gpPercent.toFixed(1)}% GP
                </span>
              )}
              <span className="text-gray-500 ml-1">
                {formatCurrency(data.rawValue)}
              </span>
              <span className="text-gray-400 ml-1">
                ({data.percentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Expense Tooltip ---
const ExpenseTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    if (data.isRemainder) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1">{data.label}</p>
        <p className="text-xs text-gray-400 mb-1">{data.accountCode}</p>
        <p className="text-sm text-gray-600">
          Expense:{' '}
          <span className="font-medium text-red-600">
            {formatCurrency(data.rawAmount)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          % of Gross: <span className="font-medium">{data.pctOfGross.toFixed(2)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

// --- Expense Legend ---
const ExpenseLegend = (props: any) => {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-col justify-center gap-2 pl-2">
      {payload
        .filter((entry: any) => !entry.payload.isRemainder)
        .map((entry: any, index: number) => {
          const data = entry.payload;
          return (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <div className="text-sm">
                <span className="text-gray-700">{data.label}</span>
                <span className="text-gray-500 ml-1">
                  {formatCurrency(data.rawAmount)}
                </span>
                <span className="text-gray-400 ml-1">
                  ({data.pctOfGross.toFixed(1)}%)
                </span>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export const PartsGrossBreakdown: React.FC<PartsGrossBreakdownProps> = ({
  partsGrossByPayType,
  partsSalesByPayType,
  partsControllableExpenses,
  totalPartsGross,
  year,
  month,
  collapsed = false,
  onToggleCollapse,
  lastRefreshed,
}) => {
  const chartData = useMemo(() => {
    if (!partsGrossByPayType) return [];

    const total = PAY_TYPE_CONFIG.reduce(
      (sum, pt) => sum + (partsGrossByPayType[pt.key] || 0),
      0
    );
    if (total === 0) return [];

    return PAY_TYPE_CONFIG
      .map(pt => {
        const rawValue = partsGrossByPayType[pt.key] || 0;
        const salesValue = partsSalesByPayType?.[pt.key as keyof PartsSalesByPayType] || 0;
        return {
          key: pt.key,
          label: pt.label,
          // Use absolute value for pie sizing (inventory adjustment can be negative)
          value: Math.abs(rawValue),
          rawValue,
          salesValue,
          percentage: (rawValue / total) * 100,
          gpPercent: salesValue > 0 && pt.key !== 'otherParts' ? (rawValue / salesValue) * 100 : null,
          color: pt.color,
        };
      })
      .filter(d => d.value !== 0);
  }, [partsGrossByPayType, partsSalesByPayType]);

  const payTypeTotal = useMemo(() => {
    if (!partsGrossByPayType) return 0;
    return PAY_TYPE_CONFIG.reduce(
      (sum, pt) => sum + (partsGrossByPayType[pt.key] || 0),
      0
    );
  }, [partsGrossByPayType]);

  const totalGpPercent = useMemo(() => {
    if (!partsGrossByPayType || !partsSalesByPayType) return null;
    const salesBearingKeys = [
      'customerParts',
      'warrantyParts',
      'internalParts',
      'counterRetail',
      'counterWholesale',
    ] as const;
    const grossSum = salesBearingKeys.reduce(
      (s, k) => s + (partsGrossByPayType[k] || 0),
      0
    );
    const salesSum = salesBearingKeys.reduce(
      (s, k) => s + (partsSalesByPayType[k] || 0),
      0
    );
    return salesSum > 0 ? (grossSum / salesSum) * 100 : null;
  }, [partsGrossByPayType, partsSalesByPayType]);

  // --- Expense Chart Data ---
  const expenseChartData = useMemo(() => {
    if (!partsControllableExpenses || totalPartsGross <= 0) return [];

    const expenseSlices = partsControllableExpenses
      .filter(e => e.amount !== 0)
      .map(e => ({
        key: e.accountCode,
        accountCode: e.accountCode,
        label: e.description,
        value: Math.abs(e.amount),
        rawAmount: e.amount,
        pctOfGross: (e.amount / totalPartsGross) * 100,
        color: EXPENSE_COLORS[e.accountCode] || '#6b7280',
        isRemainder: false,
      }));

    if (expenseSlices.length === 0) return [];

    const totalExpenses = expenseSlices.reduce((sum, e) => sum + e.value, 0);
    const remainingGross = Math.max(0, totalPartsGross - totalExpenses);

    return [
      ...expenseSlices,
      {
        key: 'remaining',
        accountCode: '',
        label: 'Remaining Gross',
        value: remainingGross,
        rawAmount: remainingGross,
        pctOfGross: (remainingGross / totalPartsGross) * 100,
        color: REMAINING_GROSS_COLOR,
        isRemainder: true,
      },
    ];
  }, [partsControllableExpenses, totalPartsGross]);

  const totalExpenseAmount = useMemo(() => {
    if (!partsControllableExpenses) return 0;
    return partsControllableExpenses.reduce((sum, e) => sum + e.amount, 0);
  }, [partsControllableExpenses]);

  const hasExpenseData = expenseChartData.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CogIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Parts Department Analysis
            </h3>
            <p className="text-sm text-gray-500">
              {getMonthName(month)} {year}
              {lastRefreshed && (
                <span className="ml-2 text-xs text-gray-400">
                  Data as of {new Date(lastRefreshed).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(lastRefreshed).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {!collapsed && <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: Gross by Pay Type */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Gross by Pay Type</h4>
            {chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No parts gross data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="40%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="label"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`pt-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PayTypeTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      content={PayTypeLegend}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-sm text-gray-500 -mt-2">
                  Total: {formatCurrency(payTypeTotal)}
                  {totalGpPercent !== null && (
                    <>
                      {' · '}
                      <span className={totalGpPercent >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {totalGpPercent.toFixed(1)}% GP
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right column: Controllable Expenses as % of Gross */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Controllable Expenses</h4>
            {!hasExpenseData ? (
              <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
                No expense data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expenseChartData}
                      cx="40%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={1}
                      dataKey="value"
                      nameKey="label"
                    >
                      {expenseChartData.map((entry, index) => (
                        <Cell
                          key={`exp-${index}`}
                          fill={entry.color}
                          stroke={entry.isRemainder ? '#d1d5db' : undefined}
                          strokeWidth={entry.isRemainder ? 1 : 0}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<ExpenseTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      content={ExpenseLegend}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-sm text-gray-500 -mt-2">
                  Total: {formatCurrency(totalExpenseAmount)} ({totalPartsGross > 0 ? ((totalExpenseAmount / totalPartsGross) * 100).toFixed(1) : '0'}% of gross)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>}
    </div>
  );
};
